import React, { useMemo, useRef, useEffect, useState } from "react";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import { Loader2 } from "lucide-react";
import {
  ClassicEditor,
  Essentials,
  Paragraph,
  Heading,
  Bold,
  Italic,
  Underline,
  Strikethrough,
  Subscript,
  Superscript,
  Code,
  CodeBlock,
  FontFamily,
  FontSize,
  FontColor,
  FontBackgroundColor,
  Highlight,
  Alignment,
  List,
  ListProperties,
  TodoList,
  Indent,
  IndentBlock,
  Link,
  AutoLink,
  LinkImage,
  BlockQuote,
  Table,
  TableToolbar,
  TableProperties,
  TableCellProperties,
  TableCaption,
  TableColumnResize,
  Image,
  ImageToolbar,
  ImageCaption,
  ImageStyle,
  ImageResize,
  ImageInsert,
  AutoImage,
  MediaEmbed,
  MediaEmbedToolbar,
  HorizontalLine,
  PageBreak,
  SpecialCharacters,
  SpecialCharactersEssentials,
  SpecialCharactersCurrency,
  SpecialCharactersMathematical,
  SpecialCharactersArrows,
  SpecialCharactersLatin,
  SpecialCharactersText,
  FindAndReplace,
  RemoveFormat,
  SelectAll,
  ShowBlocks,
  SourceEditing,
  Undo,
  Autoformat,
} from "ckeditor5";

import "ckeditor5/ckeditor5.css";

export interface RichTextEditorProps {
  label?: React.ReactNode;
  subLabel?: React.ReactNode;
  labelClassName?: string;
  value: string;
  onChange: (data: string) => void;
  placeholder?: string;
  disabled?: boolean;
  error?: string;
  helperText?: string;
  minHeight?: string;
  className?: string;
  containerClassName?: string;
  required?: boolean;
  isLoading?: boolean;
  loadingText?: string;
}

// Helper to normalize HTML strings for reliable equality comparisons
const normalizeHtml = (html: string | undefined | null): string => {
  if (!html) return "";
  return html
    .replace(/\r\n/g, "\n")
    .replace(/\n+/g, "")
    .replace(/\s+/g, " ")
    .replace(/>\s+</g, "><")
    .replace(/<p><\/p>/g, "")
    .replace(/<p>&nbsp;<\/p>/g, "")
    .replace(/<br\s*\/?>/g, "")
    .trim();
};

// Helper to sanitize incoming HTML and strip raw unmanaged embed wrappers
const cleanEditorHtml = (html: string | undefined | null): string => {
  if (!html) return "";
  let clean = html;
  // Strip out executable script and style tags
  clean = clean.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, "");
  clean = clean.replace(/<style\b[^<]*(?:(?!<\/style>)<[^<]*)*<\/style>/gi, "");
  // Unwrap legacy raw embed wrappers that cause ViewDowncastWriter RawElement breakage
  clean = clean.replace(/<div\b[^>]*class=["'][^"']*raw-html-embed[^"']*["'][^>]*>([\s\S]*?)<\/div>/gi, "$1");
  clean = clean.replace(/<div\b[^>]*class=["'][^"']*html-object-embed[^"']*["'][^>]*>([\s\S]*?)<\/div>/gi, "$1");
  return clean;
};

export const RichTextEditor: React.FC<RichTextEditorProps> = ({
  label,
  subLabel,
  labelClassName = "",
  value,
  onChange,
  placeholder,
  disabled = false,
  error,
  helperText,
  minHeight = "180px",
  className = "",
  containerClassName = "",
  required = false,
  isLoading = false,
  loadingText,
}) => {
  const editorConfig = useMemo(() => {
    return {
      licenseKey: "GPL",
      plugins: [
        Essentials,
        Paragraph,
        Heading,
        Bold,
        Italic,
        Underline,
        Strikethrough,
        Subscript,
        Superscript,
        Code,
        CodeBlock,
        FontFamily,
        FontSize,
        FontColor,
        FontBackgroundColor,
        Highlight,
        Alignment,
        List,
        ListProperties,
        TodoList,
        Indent,
        IndentBlock,
        Link,
        AutoLink,
        LinkImage,
        BlockQuote,
        Table,
        TableToolbar,
        TableProperties,
        TableCellProperties,
        TableCaption,
        TableColumnResize,
        Image,
        ImageToolbar,
        ImageCaption,
        ImageStyle,
        ImageResize,
        ImageInsert,
        AutoImage,
        MediaEmbed,
        MediaEmbedToolbar,
        HorizontalLine,
        PageBreak,
        SpecialCharacters,
        SpecialCharactersEssentials,
        SpecialCharactersCurrency,
        SpecialCharactersMathematical,
        SpecialCharactersArrows,
        SpecialCharactersLatin,
        SpecialCharactersText,
        FindAndReplace,
        RemoveFormat,
        SelectAll,
        ShowBlocks,
        SourceEditing,
        Undo,
        Autoformat,
      ],
      toolbar: {
        items: [
          "undo",
          "redo",
          "|",
          "sourceEditing",
          "showBlocks",
          "findAndReplace",
          "selectAll",
          "|",
          "heading",
          "|",
          "fontFamily",
          "fontSize",
          "fontColor",
          "fontBackgroundColor",
          "highlight",
          "|",
          "bold",
          "italic",
          "underline",
          "strikethrough",
          "subscript",
          "superscript",
          "code",
          "removeFormat",
          "|",
          "alignment",
          "|",
          "bulletedList",
          "numberedList",
          "todoList",
          "outdent",
          "indent",
          "|",
          "link",
          "insertImage",
          "mediaEmbed",
          "insertTable",
          "blockQuote",
          "codeBlock",
          "horizontalLine",
          "pageBreak",
          "specialCharacters",
        ],
        shouldNotGroupWhenFull: true,
      },
      heading: {
        options: [
          { model: "paragraph", title: "Paragraph", class: "ck-heading_paragraph" },
          { model: "heading1", view: "h1", title: "Heading 1", class: "ck-heading_heading1" },
          { model: "heading2", view: "h2", title: "Heading 2", class: "ck-heading_heading2" },
          { model: "heading3", view: "h3", title: "Heading 3", class: "ck-heading_heading3" },
          { model: "heading4", view: "h4", title: "Heading 4", class: "ck-heading_heading4" },
          { model: "heading5", view: "h5", title: "Heading 5", class: "ck-heading_heading5" },
          { model: "heading6", view: "h6", title: "Heading 6", class: "ck-heading_heading6" },
        ],
      },
      fontFamily: {
        options: [
          "default",
          "Inter, sans-serif",
          "Arial, Helvetica, sans-serif",
          "Courier New, Courier, monospace",
          "Georgia, serif",
          "Lucida Sans Unicode, Lucida Grande, sans-serif",
          "Tahoma, Geneva, sans-serif",
          "Times New Roman, Times, serif",
          "Trebuchet MS, Helvetica, sans-serif",
          "Verdana, Geneva, sans-serif",
        ],
        supportAllValues: true,
      },
      fontSize: {
        options: [10, 12, 14, "default", 18, 20, 24, 28, 32, 36],
        supportAllValues: true,
      },
      alignment: {
        options: ["left", "center", "right", "justify"],
      },
      list: {
        properties: {
          styles: true,
          startIndex: true,
          reversed: true,
        },
      },
      table: {
        contentToolbar: [
          "tableColumn",
          "tableRow",
          "mergeTableCells",
          "tableProperties",
          "tableCellProperties",
        ],
      },
      image: {
        toolbar: [
          "imageTextAlternative",
          "toggleImageCaption",
          "|",
          "imageStyle:inline",
          "imageStyle:block",
          "imageStyle:side",
          "|",
          "linkImage",
          "|",
          "resizeImage",
        ],
        insert: {
          integrations: ["url"],
        },
      },
      link: {
        addTargetToExternalLinks: true,
        defaultProtocol: "https://",
        decorators: {
          openInNewTab: {
            mode: "manual",
            label: "Open in a new tab",
            attributes: {
              target: "_blank",
              rel: "noopener noreferrer",
            },
          },
        },
      },
      codeBlock: {
        languages: [
          { language: "plaintext", label: "Plain text" },
          { language: "javascript", label: "JavaScript" },
          { language: "typescript", label: "TypeScript" },
          { language: "html", label: "HTML" },
          { language: "css", label: "CSS" },
          { language: "python", label: "Python" },
          { language: "sql", label: "SQL" },
          { language: "json", label: "JSON" },
          { language: "bash", label: "Bash/Shell" },
          { language: "java", label: "Java" },
          { language: "csharp", label: "C#" },
          { language: "cpp", label: "C++" },
          { language: "php", label: "PHP" },
          { language: "markdown", label: "Markdown" },
        ],
      },
      placeholder: placeholder || "Write rich formatted content here...",
    };
  }, [placeholder]);

  const editorRef = useRef<any>(null);
  const isSettingDataRef = useRef<boolean>(false);
  const lastHtmlRef = useRef<string>(cleanEditorHtml(value || ""));
  const initialDataRef = useRef<string>(cleanEditorHtml(value || ""));
  const [isEditorReady, setIsEditorReady] = useState(false);
  const latestValueRef = useRef<string>(value || "");
  latestValueRef.current = value || "";

  // Synchronize truly external data updates (e.g. async fetch from API, switching lesson, AI draft, or form reset)
  // while strictly skipping updates caused by user editing or when editor is focused.
  useEffect(() => {
    if (!editorRef.current || !isEditorReady) return;

    // 1. If editor is currently focused, the user is actively typing / interacting inside it.
    // Never overwrite live user typing!
    const isFocused = editorRef.current.editing?.view?.document?.isFocused;
    if (isFocused) {
      return;
    }

    // 2. If editor is currently in Source Editing mode, calling setData will break / error.
    const isSourceEditing =
      editorRef.current.plugins?.has("SourceEditing") &&
      editorRef.current.plugins?.get("SourceEditing")?.isSourceEditingMode;
    if (isSourceEditing) {
      return;
    }

    const currentEditorData = editorRef.current.getData();
    const nextData = cleanEditorHtml(value || "");

    // 3. If nextData matches what this editor already has or what this editor emitted, do nothing!
    if (
      normalizeHtml(nextData) === normalizeHtml(lastHtmlRef.current) ||
      normalizeHtml(nextData) === normalizeHtml(currentEditorData)
    ) {
      lastHtmlRef.current = nextData;
      return;
    }

    // 4. Truly external update: load into editor cleanly
    lastHtmlRef.current = nextData;
    isSettingDataRef.current = true;
    try {
      editorRef.current.setData(nextData);
    } catch (err) {
      console.warn("CKEditor setData error caught safely:", err);
    } finally {
      setTimeout(() => {
        isSettingDataRef.current = false;
      }, 50);
    }
  }, [value, isEditorReady]);

  useEffect(() => {
    return () => {
      editorRef.current = null;
      setIsEditorReady(false);
    };
  }, []);

  return (
    <div className={`cht-flex cht-flex-col cht-w-full ${containerClassName} ${className}`}>
      {/* Label Header matching Input component */}
      {(label || subLabel) && (
        <div className="cht-flex cht-items-center cht-justify-between cht-mb-1.5 cht-px-0.5">
          {label && (
            <label
              className={`cht-flex cht-items-center cht-gap-1 ${
                labelClassName ? labelClassName : "cht-text-xs cht-font-bold cht-text-[#1e293b]"
              }`}
            >
              <span>{label}</span>
              {required && <span className="cht-text-red-500 cht-text-sm leading-none">*</span>}
            </label>
          )}
          {subLabel && (
            <span className="cht-text-[11px] cht-font-medium cht-text-gray-400">
              {subLabel}
            </span>
          )}
        </div>
      )}

      {/* Editor Container with matching Input dropshadow and focus glow */}
      <div
        className={`
          cht-rich-text-wrapper cht-relative cht-flex cht-flex-col cht-w-full cht-bg-white cht-rounded-2xl
          cht-border cht-border-solid cht-overflow-hidden cht-transition-all cht-duration-200
          cht-shadow-[0_1px_3px_rgba(0,0,0,0.06),0_1px_2px_rgba(0,0,0,0.04)]
          ${
            error
              ? "cht-border-red-400 focus-within:cht-border-red-500 cht-bg-red-50/10 focus-within:cht-shadow-[0_6px_24px_rgba(239,68,68,0.18),0_1px_3px_rgba(0,0,0,0.06)]"
              : "cht-border-slate-200 hover:cht-border-slate-300 hover:cht-shadow-[0_2px_6px_rgba(0,0,0,0.08)] focus-within:cht-border-[#3e57da] focus-within:cht-shadow-[0_6px_24px_rgba(62,87,218,0.16),0_1px_3px_rgba(0,0,0,0.06)]"
          }
          ${disabled ? "cht-opacity-60 cht-bg-gray-50 cht-cursor-not-allowed cht-shadow-none" : ""}
        `}
        style={
          {
            "--ck-min-height": minHeight,
            minHeight: minHeight,
            borderStyle: "solid",
          } as React.CSSProperties
        }
      >
        {/* Loading State & Skeleton Placeholder while data is loading or editor is initializing */}
        {(!isEditorReady || isLoading) && (
          <div
            className="cht-absolute cht-inset-0 cht-z-20 cht-flex cht-flex-col cht-bg-white/95 cht-backdrop-blur-[2px] cht-rounded-2xl cht-overflow-hidden cht-transition-all cht-duration-200"
            style={{ minHeight }}
          >
            {/* Top indeterminate accent bar */}
            <div className="cht-h-1 cht-w-full cht-bg-gradient-to-r cht-from-indigo-100 cht-via-[#3e57da] cht-to-indigo-100 cht-animate-pulse" />

            {/* Simulated Skeleton Toolbar */}
            <div className="cht-flex cht-items-center cht-justify-between cht-px-4 cht-py-2.5 cht-border-b cht-border-slate-100 cht-bg-slate-50/80">
              <div className="cht-flex cht-items-center cht-gap-2">
                <div className="cht-h-4 cht-w-20 cht-rounded-lg cht-bg-slate-200/70 cht-animate-pulse" />
                <div className="cht-h-4 cht-w-px cht-bg-slate-200" />
                <div className="cht-h-4 cht-w-6 cht-rounded-md cht-bg-slate-200/70 cht-animate-pulse" />
                <div className="cht-h-4 cht-w-6 cht-rounded-md cht-bg-slate-200/70 cht-animate-pulse" />
                <div className="cht-h-4 cht-w-6 cht-rounded-md cht-bg-slate-200/70 cht-animate-pulse" />
                <div className="cht-h-4 cht-w-px cht-bg-slate-200" />
                <div className="cht-h-4 cht-w-7 cht-rounded-md cht-bg-slate-200/70 cht-animate-pulse" />
                <div className="cht-h-4 cht-w-7 cht-rounded-md cht-bg-slate-200/70 cht-animate-pulse" />
                <div className="cht-h-4 cht-w-7 cht-rounded-md cht-bg-slate-200/70 cht-animate-pulse" />
              </div>
              <div className="cht-flex cht-items-center cht-gap-2">
                <div className="cht-h-4 cht-w-14 cht-rounded-lg cht-bg-slate-200/50 cht-animate-pulse" />
              </div>
            </div>

            {/* Skeleton Content Area with Floating Indicator Card */}
            <div className="cht-relative cht-flex-1 cht-p-5 cht-flex cht-flex-col cht-justify-between" style={{ minHeight: "140px" }}>
              <div className="cht-space-y-3.5 cht-w-full cht-pt-1">
                <div className="cht-h-3.5 cht-w-3/4 cht-rounded-md cht-bg-slate-200/70 cht-animate-pulse" />
                <div className="cht-h-3.5 cht-w-full cht-rounded-md cht-bg-slate-200/50 cht-animate-pulse" />
                <div className="cht-h-3.5 cht-w-5/6 cht-rounded-md cht-bg-slate-200/50 cht-animate-pulse" />
                <div className="cht-h-3.5 cht-w-1/2 cht-rounded-md cht-bg-slate-200/40 cht-animate-pulse" />
              </div>

              {/* Floating Loading Status Card */}
              <div className="cht-absolute cht-inset-0 cht-flex cht-items-center cht-justify-center cht-pointer-events-none">
                <div className="cht-flex cht-items-center cht-gap-3 cht-px-4 cht-py-2.5 cht-rounded-2xl cht-bg-white/95 cht-shadow-[0_4px_20px_rgba(0,0,0,0.08)] cht-border cht-border-slate-100 cht-pointer-events-auto">
                  <div className="cht-w-7 cht-h-7 cht-rounded-xl cht-bg-indigo-50 cht-flex cht-items-center cht-justify-center cht-shrink-0">
                    <Loader2 className="cht-w-4 cht-h-4 cht-animate-spin cht-text-[#3e57da]" />
                  </div>
                  <div className="cht-flex cht-flex-col">
                    <span className="cht-text-xs cht-font-bold cht-text-slate-800">
                      {loadingText || (!isEditorReady ? "Initializing editor..." : "Loading content...")}
                    </span>
                    <span className="cht-text-[10px] cht-font-medium cht-text-slate-400">
                      {!isEditorReady ? "Preparing rich text tools" : "Syncing latest data"}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        <style>{`
          .cht-rich-text-wrapper .ck-editor__editable_inline {
            min-height: var(--ck-min-height, 180px);
            max-height: 560px;
            font-size: 0.875rem;
            line-height: 1.625;
            color: #1e293b;
            padding: 1rem 1.25rem;
          }
          .cht-rich-text-wrapper .ck-source-editing-area textarea {
            min-height: var(--ck-min-height, 180px);
            max-height: 560px;
            font-size: 0.875rem;
            font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
            line-height: 1.5;
            color: #0f172a;
            padding: 1rem 1.25rem;
            background-color: #ffffff;
            border: none !important;
            outline: none !important;
            resize: vertical;
          }
          .cht-rich-text-wrapper .ck.ck-editor__top .ck-sticky-panel .ck-toolbar {
            border-top: none;
            border-left: none;
            border-right: none;
            border-bottom: 1px solid #e2e8f0;
            background-color: #f8fafc;
            border-top-left-radius: 1rem;
            border-top-right-radius: 1rem;
            padding: 0.4rem 0.6rem;
            flex-wrap: wrap;
            gap: 2px;
          }
          .cht-rich-text-wrapper .ck.ck-content.ck-focused {
            border: none !important;
            box-shadow: none !important;
          }
          .cht-rich-text-wrapper .ck.ck-editor__main > .ck-editor__editable {
            background-color: #ffffff;
            border: none !important;
            border-bottom-left-radius: 1rem;
            border-bottom-right-radius: 1rem;
          }
        `}</style>

        <CKEditor
          editor={ClassicEditor as any}
          data={initialDataRef.current}
          disabled={disabled}
          config={editorConfig as any}
          onError={(error, details) => {
            console.warn("CKEditor handled internal error:", error, details);
          }}
          onReady={(editor) => {
            editorRef.current = editor;
            setIsEditorReady(true);
            const currentData = editor.getData();
            const targetData = cleanEditorHtml(latestValueRef.current || "");
            lastHtmlRef.current = targetData;
            if (targetData && normalizeHtml(currentData) !== normalizeHtml(targetData)) {
              isSettingDataRef.current = true;
              try {
                editor.setData(targetData);
              } catch (err) {
                console.warn("CKEditor setData error in onReady caught safely:", err);
              } finally {
                setTimeout(() => {
                  isSettingDataRef.current = false;
                }, 50);
              }
            }
          }}
          onChange={(_event, editor) => {
            if (isSettingDataRef.current) {
              return;
            }
            const data = editor.getData();
            lastHtmlRef.current = data;
            latestValueRef.current = data;
            onChange(data);
          }}
        />
      </div>

      {error ? (
        <p className="cht-flex cht-items-center cht-gap-1 cht-mt-1.5 cht-px-1 cht-text-xs cht-font-semibold cht-text-red-500">
          <span>{error}</span>
        </p>
      ) : helperText ? (
        <p className="cht-mt-1.5 cht-px-1 cht-text-xs cht-text-gray-400">{helperText}</p>
      ) : null}
    </div>
  );
};

export default RichTextEditor;
